import { Text } from '@ui/themed';
import { t } from 'i18next';
import { useMemo } from 'react';
import { cn } from '../../../lib/styles';
import { FieldError } from '../field-error/field-error.spark';
import { Icon } from '../icon/icon.spark';
import { Tooltip } from '../tooltip/tooltip.spark';
import { useFieldIds } from '../use-field-ids';
import type { TextAreaProps } from './types';

export function TextArea({
  label,
  tooltip,
  description,
  onChange,
  id,
  error,
  rows = 4,
  resize = true,
  mono,
  ...inputProps
}: TextAreaProps) {
  // NOTE: this length counter only works when manually managing the state and passing through `value`
  // Without passing `value` through, this component won't re-render, so won't update the count
  const valueLength = useMemo(() => inputProps.value?.length ?? 0, [inputProps.value?.length]);

  const {
    fieldId: computedId,
    descriptionId,
    charCountId,
    errorId,
    describedBy: describedByIds,
  } = useFieldIds({
    id,
    name: inputProps.name,
    hasDescription: !!description,
    hasCharCount: !!inputProps.maxLength,
    hasError: !!error,
  });

  return (
    <div className="flex flex-col gap-1.5 text-sm text-spark-text-primary">
      {/* Label & tooltip */}
      {label && (
        <label htmlFor={computedId} className="flex items-center gap-x-1">
          {label}
          {tooltip && (
            <Tooltip content={tooltip}>
              <Icon name="infoCircle" />
            </Tooltip>
          )}
        </label>
      )}
      <div
        className={cn(
          'flex flex-col rounded-lg border border-gray-300 bg-white px-2 py-1 text-spark-text-secondary shadow-xs transition focus-within:border-spark-purple focus-within:outline-hidden focus-within:ring-spark-purple',
          {
            'bg-gray-100': inputProps.disabled,
            'border-red-300 bg-red-100 text-spark-text-danger focus-within:border-spark-text-danger':
              !!error,
          },
        )}
      >
        <textarea
          {...inputProps}
          rows={rows}
          onChange={(e) => onChange?.(e.target.value)}
          id={computedId}
          aria-describedby={describedByIds || undefined}
          aria-invalid={!!error}
          className={cn(
            'form-textarea w-full border-0 bg-transparent p-0 text-sm text-spark-text-primary placeholder-spark-text-secondary outline-hidden focus:ring-0',
            resize ? 'resize-y' : 'resize-none',
            mono && 'font-mono',
            {
              'text-spark-text-secondary': inputProps.disabled,
              'placeholder-spark-text-danger': !!error,
            },
          )}
        />
        {/* Max length counter */}
        {inputProps.maxLength && (
          <span id={charCountId} className="flex justify-end">
            {valueLength}/{inputProps.maxLength}
          </span>
        )}
      </div>

      {inputProps.maxLength && (
        <div aria-live="polite" className="sr-only">
          {t('aria:characters_remaining', { number: inputProps.maxLength - valueLength })}
        </div>
      )}

      {error && <FieldError id={errorId}>{error}</FieldError>}

      {description && (
        <div className="ms-0.5">
          <Text id={descriptionId} variant="secondary" size="sm">
            {description}
          </Text>
        </div>
      )}
    </div>
  );
}
